'use client';

import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { BasketDocument } from '@packages/gql/generated/shopping/BasketDocument';
import { PageMetaData } from '@packages/modules/src/PageMetaData/PageMetaData';
import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import React, { type ReactNode, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { CheckoutContainer } from '@packages/order/src/components/CheckoutContainer/CheckoutContainer';
import { BuyBox } from '@packages/order/src/components/BuyBox/BuyBox';
import { JoeLoyalty } from '@packages/order/src/components/JoeLoyalty/JoeLoyalty';
import { PaybackBox } from '@packages/order/src/components/PaybackBox/PaybackBox';
import { Seals } from '@packages/order/src/components/Seals/Seals';
import { BasketSkeleton } from '@packages/order/src/components/Basket/BasketSkeleton';
import { Basket } from '@packages/order/src/components/Basket/Basket.client';
import { Tracking } from '@packages/order/src/components/Tracking/Tracking';
import { PrudsysBasketProductsRecoSlider } from '@packages/recommendations/src/components/ProductsRecoSliders/PrudsysBasketProductsRecoSlider/PrudsysBasketProductsRecoSlider.client';

const messages = defineMessages({
  basketHeadline: {
    id: 'CheckoutContainer.Basket.Headline',
    defaultMessage: 'Warenkorb',
    description: 'this will be displayed above the basket as a headline',
  },
  basketStepSubline: {
    id: 'CheckoutContainer.Basket.StepSubline',
    defaultMessage: 'Schritt 1 von 5',
    description: 'this will be displayed above the basket as a stepsubline',
  },
});

/* GraphQL */ `
  query Basket($locale: String!) {
    basket(locale: $locale) {
      itemCount
      lineItems {
        product {
          akl
          orderNumber
        }
      }
      ...BasketFragment
      ...BuyBoxData
      ...BasketTrackingFragment
      loyaltyProgram {
        ...LoyaltyFragment
      }
      messages {
        ...MessageFragment
      }
    }
  }
`;

const ErrorNotification = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(true);

  if (!open) return null;

  return (
    <Notification severity="error" onClose={() => setOpen(false)}>
      {children}
    </Notification>
  );
};

const Warenkorb = () => {
  const { formatMessage } = useIntl();
  const { language, defaultLanguage } = useI18n();
  const [{ data, error }] = useAuthQuery({
    query: BasketDocument,
    variables: { locale: language || defaultLanguage || 'de' },
    requestPolicy: 'cache-and-network',
  });

  const basket = { ...data?.basket };
  const isFilledBasket = !!data?.basket.itemCount && data.basket.itemCount > 0;
  const productIds: string[] = [];
  basket?.lineItems?.forEach((lineItem) => {
    productIds.push(lineItem.product.orderNumber);
  });

  return (
    <>
      <CheckoutContainer
        step={0}
        headline={formatMessage(messages.basketHeadline)}
        stepSubline={formatMessage(messages.basketStepSubline)}
        rightContent={
          <>
            {isFilledBasket && <BuyBox data={data?.basket} />}
            {isFilledBasket && (
              <JoeLoyalty loyaltyProgramData={data?.basket.loyaltyProgram || {}} />
            )}
            {isFilledBasket && (
              <PaybackBox loyaltyProgramData={data?.basket.loyaltyProgram || {}} />
            )}
            <Seals />
          </>
        }
      >
        {/* <PageMetaData /> needs to be first component in page render flow because of timing issues with glycerin tracking */}
        <PageMetaData page={{ type: 'basket', template: 'basket' }} />
        {!data && !error && <BasketSkeleton />}
        {error && <ErrorNotification>{error.graphQLErrors[0]?.message}</ErrorNotification>}
        {data && (
          <>
            <Basket data={basket} />
            <Tracking data={basket} eecType="EECCheckout" step={1} />
          </>
        )}
      </CheckoutContainer>
      {data && (
        <PrudsysBasketProductsRecoSlider
          pageTemplate="basket"
          preferTitle
          title={formatMessage({
            id: 'CheckoutContainer.Basket.ProductRec.Headline',
            defaultMessage: 'Empfehlungen für Sie',
          })}
          fetchOnThreshold
          productIds={productIds}
          elementIndex={0}
        />
      )}
    </>
  );
};
// eslint-disable-next-line import-x/no-default-export
export default Warenkorb;
